import React from 'react';
import { Box, HorizontalScroll, Typography } from '@drone-js/core';
import { getImageUrl } from 'core/utils/activityUrlGenerator';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import ContentContainer from 'component/common/ContentContainer/ContentContainer';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import { ActivitiesWhyChooseAgodaSeenEventActivitiesFieldsV1 } from '@agoda/analytics-messages-definition-activities/events.split';
import { ActivitiesWhyChooseAgodaSeenEventActivitiesManifestV1 } from '@agoda/analytics-messages-definition-activities/manifest/events';
import { ActivityImage } from 'component/common/ActivityImage/ActivityImage';
import { FeatureName } from 'core/constant/FeatureName';
import Feature from 'component/common/Experiment/Feature';
import { CmsValue } from 'core/type/BootstrapConfig.types';

interface WhyChooseAgodaProps {
    isCompact?: boolean;
    isNewHomePageEnabled?: boolean;
}

interface WhyChooseAgodaItemWrapperProps {
    image: string;
    title: string;
    description: string;
    isCompact?: boolean;
    isNewHomePageEnabled?: boolean;
}

interface WhyChooseAgodaHorizontalScrollProps {
    cms: CmsValue;
    isCompact?: boolean;
    isNewHomePageEnabled?: boolean;
}

type WhyChooseAgodaItemProps = WhyChooseAgodaItemWrapperProps;

const WhyChooseAgodaItem: React.FC<WhyChooseAgodaItemProps> = (props) => {
    const { isCompact, title, description, image, isNewHomePageEnabled } = props;
    const alignment = isNewHomePageEnabled ? 'start' : isCompact ? 'center' : undefined;
    return (
        <Box
            borderRadius={isNewHomePageEnabled ? '16' : '0'}
            backgroundColor={isNewHomePageEnabled ? 'base.background' : 'base.surface'}
            display="flex"
            minWidth={isCompact ? '180px' : '300px'}
            width="33%"
            gap="12"
            flexDirection={isCompact ? 'column' : 'row'}
            alignItems={isNewHomePageEnabled ? 'flex-start' : 'center'}
        >
            {/* eslint-disable-next-line @agoda/agoda-a11y/no-empty-alt-image -- @agoda/agoda-a11y/no-empty-alt-image[244bc642-6ace-46e8-8bc8-05db173e5cb3] */}
            <ActivityImage
                aria-hidden="true"
                mx={isNewHomePageEnabled ? '16' : '0'}
                mt={isNewHomePageEnabled ? '12' : '0'}
                width="64px"
                height="64px"
                src={getImageUrl(image)}
                alt=""
            />
            <Box display="flex" mb={isNewHomePageEnabled ? '12' : '0'} mx={isNewHomePageEnabled ? '16' : '0'} flexDirection="column" justifyContent="center" alignItems={alignment}>
                <Typography variant="body-default-strong" textColor="base.default" textAlign={alignment}>
                    {title}
                </Typography>
                <Typography variant={isCompact ? 'caption-default' : 'body-s'} textColor="base.default" textAlign={alignment}>
                    {description}
                </Typography>
            </Box>
        </Box>
    );
};
WhyChooseAgodaItem.displayName = 'WhyChooseAgodaItem';

const WhyChooseAgodaHorizontalScroll: React.FC<WhyChooseAgodaHorizontalScrollProps> = (props) => {
    const { cms, isCompact, isNewHomePageEnabled } = props;

    return (
        <HorizontalScroll display="flex" flexDirection="row" gap="24" py={isCompact ? '4' : undefined} nextLabel={cms[130706]} previousLabel={cms[130765]}>
            <WhyChooseAgodaItem title={cms[292307]} description={cms[292308]} image="agoji-city-page-1-m.png" isCompact={isCompact} isNewHomePageEnabled={isNewHomePageEnabled} />
            <WhyChooseAgodaItem title={cms[292309]} description={cms[292310]} image="agoji-city-page-2-m.png" isCompact={isCompact} isNewHomePageEnabled={isNewHomePageEnabled} />
            <WhyChooseAgodaItem title={cms[292311]} description={cms[292312]} image="agoji-city-page-3-m.png" isCompact={isCompact} isNewHomePageEnabled={isNewHomePageEnabled} />
        </HorizontalScroll>
    );
};
WhyChooseAgodaHorizontalScroll.displayName = 'WhyChooseAgodaHorizontalScroll';

const WhyChooseAgoda: React.FC<WhyChooseAgodaProps> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const isPageContainerSpacing = useSelector(selectFeatureBehaviorFromBootstrap).PageContainerSpacing;
    const { isCompact, isNewHomePageEnabled } = props;

    React.useEffect(() => {
        trackElementEvent<ActivitiesWhyChooseAgodaSeenEventActivitiesFieldsV1>(ActivitiesWhyChooseAgodaSeenEventActivitiesManifestV1.ids, {});
    }, []);

    return (
        <Box
            data-testid="activities-why-choose-agoda"
            data-element-name="activities-why-choose-agoda"
            backgroundColor="base.surface"
            display="flex"
            flexDirection="column"
            overflowX="auto"
            pb={isCompact ? '0' : '16'}
            pt={isPageContainerSpacing ? undefined : isCompact ? '0' : '32'}
            width="100%"
        >
            <Feature.Off name={FeatureName.PageContainerSpacing}>
                <ContentContainer
                    display="flex"
                    flexDirection="column"
                    flex={1}
                    enforceDefaultSpacing={true}
                    isFullContentWidth={isNewHomePageEnabled}
                    mb={isCompact ? '0' : '32'}
                    maxWidth="100%"
                >
                    <Box display="flex" mb="12">
                        <Typography variant="heading-default" textColor="base.default">
                            {cms[292306]}
                        </Typography>
                    </Box>
                    <WhyChooseAgodaHorizontalScroll cms={cms} isCompact={isCompact} isNewHomePageEnabled={isNewHomePageEnabled} />
                </ContentContainer>
            </Feature.Off>
            <Feature.On name={FeatureName.PageContainerSpacing}>
                {!isCompact ? (
                    <ContentContainer
                        display="flex"
                        flexDirection="column"
                        flex={1}
                        enforceDefaultSpacing={true}
                        isFullContentWidth={isNewHomePageEnabled}
                        mb={isCompact ? '0' : '32'}
                    >
                        <Box display="flex" mb="12">
                            <Typography variant="heading-default" textColor="base.default">
                                {cms[292306]}
                            </Typography>
                        </Box>
                        <WhyChooseAgodaHorizontalScroll cms={cms} isCompact={isCompact} isNewHomePageEnabled={isNewHomePageEnabled} />
                    </ContentContainer>
                ) : (
                    <>
                        <Box display="flex" mb="12">
                            <Typography variant="heading-default" textColor="base.default">
                                {cms[292306]}
                            </Typography>
                        </Box>
                        <WhyChooseAgodaHorizontalScroll cms={cms} isCompact={isCompact} isNewHomePageEnabled={isNewHomePageEnabled} />
                    </>
                )}
            </Feature.On>
        </Box>
    );
};
WhyChooseAgoda.displayName = 'WhyChooseAgoda';

export default WhyChooseAgoda;
